import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "form-card-tabs-container px-48" }
const _hoisted_2 = { class: "columns-2 mt-6" }
const _hoisted_3 = { class: "columns-2 mt-6" }
const _hoisted_4 = {
  class: "columns-2 mt-6 flex gap-4 items-end",
  ref: "scrollUp"
}
const _hoisted_5 = { class: "w-1/2" }
const _hoisted_6 = { class: "text-gray-700 font-medium text-sm" }
const _hoisted_7 = {
  key: 0,
  class: "absolute z-10 top-[100%] font-normal text-sm pt-1 text-red-500"
}
const _hoisted_8 = { class: "relative" }
const _hoisted_9 = {
  key: 0,
  "data-testid": "region-guard",
  role: "alert",
  class: "mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
}
const _hoisted_10 = { class: "columns-2 mt-6" }
const _hoisted_11 = { class: "columns-2 mt-6" }
const _hoisted_12 = { class: "columns-2 mt-6" }
const _hoisted_13 = { class: "mt-6" }
const _hoisted_14 = {
  key: 1,
  role: "alert",
  class: "mt-4 text-sm text-red-600"
}
const _hoisted_15 = { class: "flex items-end justify-end mt-3" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "font-semibold text-sm" }
const _hoisted_18 = {
  key: 0,
  class: "flex items-center justify-center gap-x-1"
}
const _hoisted_19 = { key: 1 }

import { computed, ref, watch } from 'vue';
import { useSchoolStore } from '@/stores/schoolStore';
import { useFilterStore } from '@/stores/filterStore';
import i18n from '@/plugins/i18n';
import { countryOptions } from '@/helper/countries';
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/vue/20/solid";
import omitBy from 'lodash/omitBy';
import isEmpty from 'lodash/isEmpty';
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/vue/20/solid";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/vue";
import { Admin } from '@/interfaces/admin.interface';
import { useProductStore } from '@/stores/productStore';
import { createDefaultSchoolObject } from '@/modules/main/components/constants/defaults';
import  SchoolChampionCreation  from "@/modules/main/components/internal-tools/SchoolChampionCreation.vue";
import { getValidationMessages } from "@formkit/validation";
import { getNode } from '@formkit/core';
import { showToast } from '@/utils/toastUtils';
import { useI18n } from 'vue-i18n';
import { useSubjectStore } from '@/stores/subjectStore';
import { Subject } from '@/interfaces/subject.interface';
import { useUserStore } from '@/stores/userStore';
import { deploymentRegion, wrongRegionFor } from '@/lib/region-country';

interface SearchOption {
  id: string;
  name: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddSchoolComponent',
  setup(__props) {

const { t } = useI18n();

const notifySuccessAddSchool = () => {
  showToast({
    text: t('internal_tools.add_school.school_message'),
    type: 'success',
    icon: CheckCircleIcon,
  })
}

const schoolStore = useSchoolStore();
schoolStore.schoolCreationError = "";
const filterStore = useFilterStore();
const productStore = useProductStore();
const subjectStore = useSubjectStore();
// The subject list is loaded on sign-in; if this form opens before it has
// arrived (or after it failed), load it here rather than offer no subjects.
if (!subjectStore.getSubjects.length) {
  subjectStore.fetchSubjects().catch(() => undefined);
}

const selectedOption = ref<SearchOption | null>(null);
const showRequired = ref(false);
const searchQuery = ref("");
const placeholder = ref(i18n.global.t("internal_tools.select").toString());
const updateSuccess = ref(false);
const responseMessage = ref("");
const newAdmins = ref<Admin[]>([]);
const newSchoolId = ref("");
const showAdminSection = ref(false);
const schoolObject = ref(createDefaultSchoolObject(filterStore.selectedFilters.schoolGroup as string, productStore.products)) as any;
const formErrors = ref<any>([]);
schoolObject.value.subjectsAvailable = "";

// A school is created in the region its country belongs to. Checked here as
// well as by dashboard-api, so nothing is created before the refusal.
const userStore = useUserStore();
const here = ref<string | null>(null);
deploymentRegion().then(region => {
  here.value = region;
});
const wrongRegion = computed(() => wrongRegionFor(schoolObject.value?.location?.country, here.value));
const switchRegion = () => userStore.forceRegionReauth();
  
// Computed filtered options based on `searchQuery`
const filteredOptions = computed(() =>
  searchQuery.value === ""
    ? countryOptions
    : countryOptions.filter((o) =>
      o.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
);

// Computed property for FormKit select options
const localeOptions = computed(() =>
  schoolStore.getLocales.map(locale => ({
    label: locale.name,
    value: locale.code
  }))
);

const learningModeOptions = computed(() =>
  schoolStore.getLearningModes.map(mode => ({
    label: mode.label,
    value: mode.value
  }))
);

const lingoModeOptions = computed(() => [
  { label: 'Lingo v1 only', value: 'v1' },
  { label: 'Lingo v2 only', value: 'v2' },
  { label: 'Both v1 and v2', value: 'both' },
]);

// Function to handle form submission when the form is invalid
const onFormInvalid = (node: any) => {
  const validations = getValidationMessages(node);

  validations.forEach((inputMessages) => {
    formErrors.value = formErrors.value.concat(
      inputMessages.map((message) => message.value)
    );
  });
  // Optionally, you can display a notification or highlight errors
  console.warn("Form submission failed due to validation errors:", validations);
};

// Change coutry casing
const formatCountryName = (country: string) => {

  //check if the country is in the countryOptions
  const countryExist = countryOptions.find((o) => o.id === country);

  //if the country is not in the countryOptions, return the selectedOption value
  if (!countryExist) {
    return selectedOption.value?.name;
  }

  return country
    ? country
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    : '';
}

watch(() => schoolObject.value.location.country, (newVal) => {
  if (newVal && typeof newVal === 'string' && newVal.trim().length > 0) {
    showRequired.value = false
  }
})

const handleSubmit = async () => {

  // Check if required fields are filled
  if (!schoolObject.value?.location.country || schoolObject.value.location.country.trim().length === 0) {
    showRequired.value = true;
    return;
  }

  if (wrongRegion.value) {
    return;
  }

  // Extract month and day from academicYearStart and academicYearEnd
  const formatDate = (dateString: any) => {
    const dateParts = dateString.split('-');
    if (dateParts.length === 3) {
      const month = dateParts[1];
      const day = dateParts[2];
      return `${month}-${day}`; // Return MM-DD
    }
    return dateString;
  };

  // Date inputs require YYYY-MM-DD. Convert only the outgoing values so a
  // failed request leaves the entered dates visible and ready to retry.
  const preparedPayload = omitBy({
    ...schoolObject.value,
    academicYearStart: formatDate(schoolObject.value.academicYearStart),
    academicYearEnd: formatDate(schoolObject.value.academicYearEnd),
  }, (value) => isEmpty(value) && typeof value === 'string');

  // Create a new school with the prepared payload
  const result = await schoolStore.createSchool(preparedPayload);

  // Add new admins to the school and handle the success message if the school was created
  if (result) {
    showAdminSection.value = true;
    newSchoolId.value = result._id;
    responseMessage.value = i18n.global.t("internal_tools.add_school.school_message").toString();
    updateSuccess.value = true;
    notifySuccessAddSchool();

    // Update the filter store with new school.
    filterStore.addSchoolToSchoolGroup(result, filterStore.selectedFilters.schoolGroup as string, true);

    getNode('new-school-creation')?.reset();
    // Clear the form data after successful creation
    clearData();
  }
};

// Watch `selectedOption` to update `searchQuery` and bind to `schoolObject` country ID
watch(selectedOption, (newOption) => {
  searchQuery.value = newOption ? newOption.name : "";
  schoolObject.value.location.country = newOption?.id || "";
});

const isSchoolObjectComplete = computed(() => {
  const obj = schoolObject.value;

  if (
    !obj.name ||
    !obj.domain ||
    !obj.subjectsAvailable ||
    !obj.schoolGroup ||
    !obj.academicYearStart ||
    !obj.academicYearEnd ||
    !obj.preferredLang
  ) return false;

  if (
    !obj.location.city ||
    !obj.location.country ||
    !obj.location.street
  ) return false;

  // All checks passed
  return true;
});

const subjectsOptions = computed(() => {
  const subjects = subjectStore.getSubjects;

  const options = [];

  options.push({
    label: i18n.global.t("internal_tools.form.select_subject"),
    value: "",
    attrs: { disabled: true }
  });

  // Add individual subjects
  subjects.forEach(subject => {
    options.push({
      label: subject.name,
      value: [subject.id],
    });
  });

  // Add combined option if at least two subjects exist
  if (subjects.length >= 2) {
    const combinedLabel = `${subjects[0].name} and ${subjects[1].name}`;
    const combinedValue = [subjects[0].id, subjects[1].id];

    options.push({
      label: combinedLabel,
      value: combinedValue,
    });
  }

  return options;
});

const clearData = () => {
  // Clear the newAdmins array
  newAdmins.value = [];

  //trying to clear the country value
  selectedOption.value = { id: "", name: "" };
  
  //clear success message
  updateSuccess.value = false;
  formErrors.value = [];
  schoolStore.schoolCreationError = "";
};

watch(() => filterStore.selectedFilters.schoolGroup, clearData);

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!
  const _component_spinner_loader = _resolveComponent("spinner-loader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_FormKit, {
      type: "form",
      id: "new-school-creation",
      actions: false,
      onSubmit: handleSubmit,
      onSubmitInvalid: onFormInvalid,
      "validation-errors": formErrors.value
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_FormKit, {
            type: "text",
            label: _ctx.$t('internal_tools.form.school_name'),
            placeholder: _ctx.$t('internal_tools.form.school_name'),
            modelValue: schoolObject.value.name,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((schoolObject.value.name) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.school_name')
          }
          }, null, 8, ["label", "placeholder", "modelValue", "validation-messages"]),
          _createVNode(_component_FormKit, {
            type: "select",
            name: "subjects",
            options: subjectsOptions.value,
            label: _ctx.$t('internal_tools.form.subject'),
            placeholder: _ctx.$t('internal_tools.form.select_subject'),
            style: { color: 'text-gray-500', height: '3.5rem' },
            modelValue: schoolObject.value.subjectsAvailable,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((schoolObject.value.subjectsAvailable) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.subject')
        }
          }, null, 8, ["options", "label", "placeholder", "modelValue", "validation-messages"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_FormKit, {
            type: "text",
            label: _ctx.$t('internal_tools.form.school_location'),
            placeholder: _ctx.$t('internal_tools.form.school_location'),
            modelValue: schoolObject.value.location.street,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((schoolObject.value.location.street) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.school_location')
          }
          }, null, 8, ["label", "placeholder", "modelValue", "validation-messages"]),
          _createVNode(_component_FormKit, {
            type: "text",
            label: _ctx.$t('internal_tools.form.domain_name'),
            placeholder: _ctx.$t('internal_tools.form.domain_name'),
            modelValue: schoolObject.value.domain,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((schoolObject.value.domain) = $event)),
            validation: [
            ['required'],
            [
              'matches',
              /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
            ],
          ],
            "validation-messages": {
            required:
              _ctx.$t('internal_tools.add_school.domain_name'),
            matches:
              _ctx.$t('internal_tools.add_school.domain_example'),
          }
          }, null, 8, ["label", "placeholder", "modelValue", "validation", "validation-messages"])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_FormKit, {
              type: "text",
              label: _ctx.$t('internal_tools.form.city'),
              "outer-class": "primary-select w-1/2",
              placeholder: _ctx.$t('internal_tools.form.city_name'),
              modelValue: schoolObject.value.location.city,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((schoolObject.value.location.city) = $event)),
              validation: "required",
              "validation-messages": {
              required: _ctx.$t('internal_tools.add_school.city_name')
            }
            }, null, 8, ["label", "placeholder", "modelValue", "validation-messages"])
          ]),
          _createVNode(_unref(Combobox), {
            class: "w-1/2 relative",
            as: "div",
            modelValue: selectedOption.value,
            "onUpdate:modelValue": [
              _cache[7] || (_cache[7] = ($event: any) => ((selectedOption).value = $event)),
              _cache[8] || (_cache[8] = ($event: any) => (searchQuery.value = ''))
            ],
            label: _ctx.$t('internal_tools.form.city')
          }, {
            default: _withCtx(() => [
              _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('internal_tools.form.country_title')), 1),
              (showRequired.value == true)
                ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$t('internal_tools.add_school.couunty_name')), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_8, [
                _createVNode(_unref(ComboboxInput), {
                  class: "w-full rounded-md border-0 bg-white py-[18px] pl-3 pr-10 text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 font-normal text-base",
                  placeholder: placeholder.value,
                  onChange: _cache[5] || (_cache[5] = ($event: any) => (searchQuery.value = $event.target.value)),
                  onBlur: _cache[6] || (_cache[6] = ($event: any) => (searchQuery.value = '')),
                  "display-value": () => formatCountryName(schoolObject.value.location.country) || '',
                  validation: "required"
                }, null, 8, ["placeholder", "display-value"]),
                _createVNode(_unref(ComboboxButton), { class: "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(ChevronUpDownIcon), {
                      class: "h-5 w-5 text-gray-400",
                      "aria-hidden": "true"
                    })
                  ]),
                  _: 1
                }),
                (filteredOptions.value.length > 0)
                  ? (_openBlock(), _createBlock(_unref(ComboboxOptions), {
                      key: 0,
                      class: "dropdown-options absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (option) => {
                          return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                            key: option.id,
                            value: option,
                            as: "template"
                          }, {
                            default: _withCtx(({ active, selected }) => [
                              _createElementVNode("li", {
                                class: _normalizeClass([
                  'relative cursor-default select-none py-2 pl-8 pr-4',
                  active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                ])
                              }, [
                                _createElementVNode("span", {
                                  class: _normalizeClass(['block truncate', selected && 'font-semibold'])
                                }, _toDisplayString(option.name), 3),
                                selected
                                  ? (_openBlock(), _createElementBlock("span", {
                                      key: 0,
                                      class: _normalizeClass([
                    'absolute inset-y-0 left-0 flex items-center pl-1.5',
                    active ? 'text-white' : 'text-blue-600',
                  ])
                                    }, [
                                      _createVNode(_unref(CheckIcon), {
                                        class: "h-5 w-5",
                                        "aria-hidden": "true"
                                      })
                                    ], 2))
                                  : _createCommentVNode("", true)
                              ], 2)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 1
          }, 8, ["modelValue", "label"])
        ], 512),
        (wrongRegion.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createTextVNode(_toDisplayString(_ctx.$t('internal_tools.region_guard.message', { belongs: wrongRegion.value.toUpperCase(), here: (here.value || '').toUpperCase() })) + " ", 1),
              _createElementVNode("button", {
                type: "button",
                class: "ml-2 font-semibold underline",
                onClick: switchRegion
              }, _toDisplayString(_ctx.$t('internal_tools.region_guard.switch')), 1)
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(_component_FormKit, {
            type: "select",
            name: "locale",
            options: localeOptions.value,
            label: _ctx.$t('internal_tools.form.language_title'),
            placeholder: _ctx.$t('internal_tools.form.language'),
            style: { color: 'text-gray-500', height: '3.5rem' },
            modelValue: schoolObject.value.preferredLang,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((schoolObject.value.preferredLang) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.language')
        }
          }, null, 8, ["options", "label", "placeholder", "modelValue", "validation-messages"]),
          _createVNode(_component_FormKit, {
            type: "select",
            name: "locale",
            options: learningModeOptions.value,
            label: _ctx.$t('internal_tools.form.learning_mode'),
            placeholder: _ctx.$t('internal_tools.form.learning_mode'),
            style: { color: 'text-gray-500', height: '3.5rem' },
            modelValue: schoolObject.value.learningMode,
            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((schoolObject.value.learningMode) = $event))
          }, null, 8, ["options", "label", "placeholder", "modelValue"])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createVNode(_component_FormKit, {
            type: "select",
            name: "lingoMode",
            options: lingoModeOptions.value,
            label: "Lingo Version",
            placeholder: "Select Lingo version",
            style: { color: 'text-gray-500', height: '3.5rem' },
            modelValue: schoolObject.value.lingoMode,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((schoolObject.value.lingoMode) = $event)),
            validation: "required",
            "validation-messages": { required: 'Lingo version is required' }
          }, null, 8, ["options", "modelValue"]),
          _createVNode(_component_FormKit, {
            type: "text",
            label: _ctx.$t('internal_tools.form.emis_number'),
            placeholder: _ctx.$t('internal_tools.form.emis_number'),
            modelValue: schoolObject.value.emisNumber,
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((schoolObject.value.emisNumber) = $event))
          }, null, 8, ["label", "placeholder", "modelValue"])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_component_FormKit, {
            type: "date",
            label: _ctx.$t('internal_tools.form.start_month'),
            placeholder: _ctx.$t('internal_tools.form.month'),
            modelValue: schoolObject.value.academicYearStart,
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((schoolObject.value.academicYearStart) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.start_date')
          }
          }, null, 8, ["label", "placeholder", "modelValue", "validation-messages"]),
          _createVNode(_component_FormKit, {
            type: "date",
            label: _ctx.$t('internal_tools.form.end_month'),
            placeholder: _ctx.$t('internal_tools.form.month'),
            modelValue: schoolObject.value.academicYearEnd,
            "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((schoolObject.value.academicYearEnd) = $event)),
            validation: "required",
            "validation-messages": {
            required: _ctx.$t('internal_tools.add_school.end_date')
          }
          }, null, 8, ["label", "placeholder", "modelValue", "validation-messages"])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createVNode(_component_FormKit, {
            type: "checkbox",
            label: "Exclude from Auto Archiving",
            modelValue: schoolObject.value.excludeFromAutoArchiving,
            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((schoolObject.value.excludeFromAutoArchiving) = $event))
          }, null, 8, ["modelValue"])
        ]),
        (_unref(schoolStore).schoolCreationError)
          ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(_unref(schoolStore).schoolCreationError), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            class: _normalizeClass(["px-[14px] py-[10px] text-white border focus:outline-none focus:ring-2 rounded-lg", !isSchoolObjectComplete.value || _unref(schoolStore).schoolCreationLoader
          ? 'cursor-not-allowed !bg-blue-300 border-blue-300 !opacity-100'
          : 'bg-blue-600 border-blue-600 hover:bg-blue-700 focus:ring-blue-500 ']),
            type: "submit",
            disabled: !isSchoolObjectComplete.value || _unref(schoolStore).schoolCreationLoader
          }, [
            _createElementVNode("h1", _hoisted_17, [
              (_unref(schoolStore).schoolCreationLoader)
                ? (_openBlock(), _createElementBlock("span", _hoisted_18, [
                    _createVNode(_component_spinner_loader, { color: 'text-white dark:text-white' }),
                    _createTextVNode(" " + _toDisplayString(_ctx.$t('internal_tools.add_school.create_school')), 1)
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(_ctx.$t('internal_tools.form.btnSave')), 1))
            ])
          ], 10, _hoisted_16)
        ])
      ]),
      _: 1
    }, 8, ["validation-errors"]),
    (showAdminSection.value)
      ? (_openBlock(), _createBlock(SchoolChampionCreation, {
          key: 0,
          newSchoolId: newSchoolId.value
        }, null, 8, ["newSchoolId"]))
      : _createCommentVNode("", true)
  ]))
}
}

})